// Admin · Facilities CRUD — contracted facilities with per-facility rate cards.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X as XIcon } from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";
import { autoPrefixUS } from "../../components/PhoneInput";

const FACILITY_TYPES = ["hospital", "dialysis", "snf", "clinic", "school", "other"];

const emptyFacility = () => ({
  name: "",
  facility_type: "clinic",
  address: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  billing_contact_email: "",
  contract_active: true,
  notes: "",
  rates: {
    base_fee_usd: 35,
    per_mile_usd: 3.25,
    wheelchair_surcharge_usd: 25,
    stretcher_surcharge_usd: 90,
    wait_time_per_15min_usd: 12,
    after_hours_surcharge_usd: 20,
    no_show_fee_usd: 50,
  },
});

export default function AdminFacilities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyFacility());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/facilities");
      setItems(data);
    } catch {
      toast.error("Failed to load facilities");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(emptyFacility()); };
  const startEdit = (f) => { setEditing(f.id); setForm({ ...emptyFacility(), ...f, rates: { ...emptyFacility().rates, ...(f.rates || {}) } }); };
  const cancelEdit = () => { setEditing(null); setForm(emptyFacility()); };

  const save = async () => {
    if (!form.name || form.name.length < 2) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editing === "new") {
        await apiClient.post("/facilities", form);
        toast.success("Facility added");
      } else {
        await apiClient.patch(`/facilities/${editing}`, form);
        toast.success("Facility updated");
      }
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this facility? Existing bookings keep their rate snapshot.")) return;
    try { await apiClient.delete(`/facilities/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateRate = (k, v) => setForm((f) => ({ ...f, rates: { ...f.rates, [k]: Number(v) || 0 } }));

  return (
    <div data-testid="facilities-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-bold text-ink">Contracted Facilities</h2>
        <button
          onClick={startNew}
          data-testid="add-facility-btn"
          className="inline-flex items-center gap-1.5 bg-forest hover:bg-forest-hover text-white text-sm font-semibold px-4 py-2 rounded-md"
        >
          <Plus size={14} weight="bold" /> Add facility
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && items.length === 0 && !editing && (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
          <p className="text-sm text-slate-500">No facilities yet. Add them as contracts close — each gets its own rate card.</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} className="bg-white border border-[#E2E0D8] rounded-lg p-4 flex items-start gap-3" data-testid={`facility-${f.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate_navy">{f.name}</div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brandblue/10 text-brandblue">{f.facility_type}</span>
                {!f.contract_active && <span className="text-[10px] uppercase font-bold text-slate-400">inactive</span>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{f.address || "—"}</div>
              <div className="text-xs text-slate-500">{f.contact_name} {f.contact_phone && <>· {f.contact_phone}</>} {f.contact_email && <>· {f.contact_email}</>}</div>
              {f.billing_contact_email && (
                <div className="mt-0.5 text-[11px] text-brandblue-deep" data-testid={`facility-billing-${f.id}`}>
                  Billing → {f.billing_contact_email}
                </div>
              )}
              <div className="text-[11px] text-slate-400 mt-1">
                Base ${f.rates?.base_fee_usd?.toFixed(2)} + ${f.rates?.per_mile_usd?.toFixed(2)}/mi
                · WC +${f.rates?.wheelchair_surcharge_usd?.toFixed(0)}
                · Stretcher +${f.rates?.stretcher_surcharge_usd?.toFixed(0)}
              </div>
            </div>
            <button onClick={() => startEdit(f)} className="h-8 w-8 grid place-items-center text-slate-400 hover:text-brandblue"><PencilSimple size={14} /></button>
            <button onClick={() => remove(f.id)} className="h-8 w-8 grid place-items-center text-slate-400 hover:text-red-500"><Trash size={14} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate_navy/50 grid place-items-center z-50 p-4" onClick={cancelEdit}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="facility-edit-modal">
            <div className="flex items-center justify-between border-b border-[#E2E0D8] px-6 py-4 sticky top-0 bg-white">
              <h3 className="font-heading text-lg font-bold">{editing === "new" ? "Add facility" : "Edit facility"}</h3>
              <button onClick={cancelEdit} className="h-8 w-8 grid place-items-center hover:bg-slate-100 rounded"><XIcon size={14} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Name" required>
                  <input data-testid="facility-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Type">
                  <select value={form.facility_type} onChange={(e) => update("facility_type", e.target.value)} className={inputCls}>
                    {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Address" full>
                  <input value={form.address || ""} onChange={(e) => update("address", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Contact name">
                  <input value={form.contact_name || ""} onChange={(e) => update("contact_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Contact phone">
                  <input value={form.contact_phone || ""} onChange={(e) => update("contact_phone", e.target.value)}
                         onBlur={() => update("contact_phone", autoPrefixUS(form.contact_phone))}
                         className={inputCls} />
                </Field>
                <Field label="Contact email" full>
                  <input type="email" value={form.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Billing contact email" full>
                  <input
                    type="email"
                    value={form.billing_contact_email || ""}
                    onChange={(e) => update("billing_contact_email", e.target.value)}
                    className={inputCls}
                    placeholder="billing@facility.org"
                    data-testid="facility-billing-email-input"
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    Medicaid trip-sheet PDFs are auto-emailed here when a trip completes. Falls back to the Contact email if blank.
                  </p>
                </Field>
                <Field label="Active contract" full>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.contract_active} onChange={(e) => update("contract_active", e.target.checked)} />
                    Active
                  </label>
                </Field>
              </div>
              <div>
                <h4 className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate_navy mb-2">Contract rates</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Base fee ($)"><input type="number" step="0.01" value={form.rates.base_fee_usd} onChange={(e) => updateRate("base_fee_usd", e.target.value)} className={inputCls} /></Field>
                  <Field label="Per mile ($)"><input type="number" step="0.01" value={form.rates.per_mile_usd} onChange={(e) => updateRate("per_mile_usd", e.target.value)} className={inputCls} /></Field>
                  <Field label="Wheelchair surcharge ($)"><input type="number" step="0.01" value={form.rates.wheelchair_surcharge_usd} onChange={(e) => updateRate("wheelchair_surcharge_usd", e.target.value)} className={inputCls} /></Field>
                  <Field label="Stretcher surcharge ($)"><input type="number" step="0.01" value={form.rates.stretcher_surcharge_usd} onChange={(e) => updateRate("stretcher_surcharge_usd", e.target.value)} className={inputCls} /></Field>
                  <Field label="Wait time / 15min ($)"><input type="number" step="0.01" value={form.rates.wait_time_per_15min_usd} onChange={(e) => updateRate("wait_time_per_15min_usd", e.target.value)} className={inputCls} /></Field>
                  <Field label="After-hours surcharge ($)"><input type="number" step="0.01" value={form.rates.after_hours_surcharge_usd} onChange={(e) => updateRate("after_hours_surcharge_usd", e.target.value)} className={inputCls} /></Field>
                  <Field label="No-show fee ($)" full><input type="number" step="0.01" value={form.rates.no_show_fee_usd} onChange={(e) => updateRate("no_show_fee_usd", e.target.value)} className={inputCls} /></Field>
                </div>
              </div>
              <Field label="Notes" full>
                <textarea rows={3} value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} className={inputCls} />
              </Field>
            </div>
            <div className="border-t border-[#E2E0D8] px-6 py-3 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={cancelEdit} className="text-sm font-semibold text-slate-500 hover:text-slate-700 px-4 py-2">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="facility-save" className="bg-forest hover:bg-forest-hover text-white text-sm font-semibold px-5 py-2 rounded-md disabled:opacity-50">
                {busy ? "Saving…" : "Save facility"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full text-sm border border-[#E2E0D8] rounded-md px-3 py-2 focus:outline-none focus:border-brandblue";

function Field({ label, full, required, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-slate_navy mb-1">
        {label}{required && <span className="text-terracotta ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
